/**
 * useSquadRoster — loads one team's full squad for quick capture (SE-6980).
 *
 * **Completeness is checked, not assumed, and that is the whole point.**
 * `filterPlayers` defaults to `first: 20`, so a 25-player squad silently
 * truncates and reads as "some players are missing" rather than as a paging
 * bug. That matters more here than in a normal list, because the squad size is
 * written into the report: `createMatchReport` ignores `reportTeams` on every
 * call after the first, so a short first load freezes a wrong denominator into
 * the header permanently and no later call can correct it. The hook therefore
 * pages until the rows it holds match the server's own `totalCount`, and
 * exposes `isComplete` so the caller can refuse to create the report until
 * both squads say yes.
 *
 * `totalCount` is lazily resolved on `PlayerConnection` — selecting it costs a
 * cheap COUNT, and it is the authoritative squad size. Counting the rows
 * client-side would agree with itself while disagreeing with the server.
 *
 * One call per team, never one combined call: a combined `teamIds: [home, away]`
 * renders fine but its `totalCount` is the combined total, which cannot supply
 * the two per-team squad sizes the report contract wants.
 *
 * `filterPlayers` is a LEGACY query and is not gated by the backend's
 * `matchReportV2` AppConfig flag, so this hook works on dev before MR-2.0 is
 * deployed or the flag is flipped. A green run here says nothing about the
 * capture half of the screen, which is gated and will throw `FEATURE_DISABLED`.
 * @module features/match-report-capture/hooks/useSquadRoster
 */
import type { ApolloError } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MatchReportCaptureSquadPlayerFragment } from "@/generated/graphql";
import { useMatchReportCaptureSquadQuery } from "@/generated/graphql";

/** Page size. Above any realistic squad, so most loads are a single round trip. */
export const SQUAD_PAGE_SIZE = 100;

/**
 * Ceiling on pages fetched for one squad, as a multiple of the page size.
 *
 * The loop's exit condition comes from the server, so a `totalCount` the rows
 * can never reach would otherwise mean one request per render forever. The
 * ceiling turns that server-side inconsistency into a visibly incomplete squad
 * instead of a request storm.
 */
export const MAX_SQUAD_PAGES = 10;

/** One squad row. */
export type SquadPlayer = MatchReportCaptureSquadPlayerFragment;

/** What {@link useSquadRoster} returns. */
export interface UseSquadRosterResult {
  /** Every player loaded so far, in server order. */
  readonly players: readonly SquadPlayer[];
  /**
   * The server's own count for this squad — the value that must be sent as
   * `MatchReportTeamInput.squadSize`. Null until the first page resolves.
   */
  readonly squadSize: number | null;
  /** A request is in flight (including a page beyond the first). */
  readonly isLoading: boolean;
  /**
   * Every player the server says exists has been loaded. The report must not
   * be created until this is true for BOTH squads.
   */
  readonly isComplete: boolean;
  /**
   * Paging stopped with rows still missing — either the row ceiling was hit or
   * the server offered no next page while reporting a higher `totalCount`. The
   * squad is short and must not be treated as authoritative.
   */
  readonly isTruncated: boolean;
  /** The query error, if the load failed. */
  readonly error?: ApolloError;
}

/**
 * Loads a team's complete squad, paging until the server agrees it is whole.
 * @param teamId - Team whose squad to load; omit to skip the query entirely
 * @returns The squad, its authoritative size, and completeness signals
 */
const useSquadRoster = (teamId?: string): UseSquadRosterResult => {
  const filter = useMemo(
    () => ({ teamIds: teamId === undefined ? [] : [teamId] }),
    [teamId]
  );

  const { data, loading, error, fetchMore } = useMatchReportCaptureSquadQuery({
    variables: { filter, first: SQUAD_PAGE_SIZE },
    skip: teamId === undefined,
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.filterPlayers;

  const players = useMemo(
    () => connection?.edges.map(edge => edge.node) ?? [],
    [connection?.edges]
  );

  const squadSize = connection?.totalCount ?? null;

  // A null totalCount means the server did not answer the count, so there is
  // nothing to check completeness against. Treat a resolved page with no count
  // as complete rather than paging forever against an unknowable target.
  const isComplete =
    connection !== undefined &&
    (squadSize === null || players.length >= squadSize);

  const hasNextPage = connection?.pageInfo.hasNextPage === true;

  // The row ceiling is derived from the rows themselves rather than counted in
  // a ref, so nothing is read or written during render and there is no state to
  // reset when the team changes.
  //
  // This used to also claim the runaway case was handled structurally, because
  // the effect below re-runs only when `endCursor` changes. That argument is
  // real but it rests on `fetchMore` being referentially stable — an Apollo
  // implementation detail this hook does not control, and if it ever churns per
  // render the effect re-fires on every render against the same cursor. The
  // cursor ledger below makes the stop a mechanism instead of an argument.
  const hasHitPageCap = players.length >= SQUAD_PAGE_SIZE * MAX_SQUAD_PAGES;

  const shouldFetchMore =
    !loading &&
    !isComplete &&
    !hasHitPageCap &&
    hasNextPage &&
    connection?.pageInfo.endCursor != null;

  /**
   * Whether a page request was rejected.
   *
   * Paging stops here, and it must stop VISIBLY: the squad size is written into
   * the report once and cannot be corrected afterwards, so a silently short
   * roster freezes a wrong denominator permanently. This feeds `isTruncated`
   * for exactly that reason.
   */
  /**
   * Cursors already requested, and the team they belong to.
   *
   * A ref, not state: this is bookkeeping the render output never reads, and
   * putting it in state would make every recorded cursor a re-render — and a
   * setState reachable from the paging effect, which the hooks lint rightly
   * refuses.
   *
   * The team travels WITH the ledger so it resets itself on the first request
   * for a new squad. Resetting it in an effect instead would run after the
   * paging effect on that render, wiping the cursor just recorded and asking
   * for the same page twice.
   */
  const attemptedCursors = useRef<{
    readonly team?: string;
    readonly cursors: readonly string[];
  }>({ team: teamId, cursors: [] });
  const [hasPagingFailed, setHasPagingFailed] = useState(false);
  const [pagingTeam, setPagingTeam] = useState(teamId);

  // React's adjust-state-during-render, not an effect: a failure belonging to
  // the previous team must not mark the next team's squad truncated.
  if (pagingTeam !== teamId) {
    setPagingTeam(teamId);
    setHasPagingFailed(false);
  }

  // Truncated means "paging has stopped and the squad is still short", which
  // happens two ways: the row ceiling was hit, or the server said there is no
  // next page while still reporting a higher totalCount. Both leave a wrong
  // denominator one call away from being written, so both must be visible.
  const isTruncated =
    connection !== undefined &&
    !isComplete &&
    !loading &&
    (hasHitPageCap || !hasNextPage || hasPagingFailed);

  /**
   * Cursors this hook has already asked for.
   *
   * A server that repeats an `endCursor` — or returns a page of zero rows while
   * still reporting `hasNextPage` — would otherwise leave every paging
   * precondition true forever. Asking once per cursor makes that terminate by
   * construction, whatever the effect's dependencies happen to do.
   */

  const loadNextPage = useCallback(
    (after: string) => {
      const seen =
        attemptedCursors.current.team === teamId
          ? attemptedCursors.current.cursors
          : [];
      if (seen.includes(after)) return;
      attemptedCursors.current = { team: teamId, cursors: [...seen, after] };

      void fetchMore({ variables: { after } }).catch(() => {
        // The hook already surfaces `error`; a rejected fetchMore here would
        // otherwise become an unhandled rejection and crash the screen.
        setHasPagingFailed(true);
      });
    },
    [fetchMore, teamId]
  );

  useEffect(() => {
    const cursor = connection?.pageInfo.endCursor;
    if (!shouldFetchMore || cursor == null) return;
    loadNextPage(cursor);
  }, [shouldFetchMore, connection?.pageInfo.endCursor, loadNextPage]);

  return {
    players,
    squadSize,
    isLoading: loading,
    isComplete,
    isTruncated,
    error,
  };
};

export default useSquadRoster;
